import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Resource from './Resource';
import Unit from './Unit';
import {RESOURCE_CATEGORIES} from '@/../../app/src/helpers/constants';

export default class Equipment extends BaseModel {
  constructor({
    id,
    number = undefined,
    designation = undefined,
    resource = undefined,
    active = true,
    tags = [],
    planning = false,
    serialNumber = undefined,
    chassisNumber = undefined,
    registrationNumber = undefined,
    memo = undefined,
    model = undefined,
    power = undefined,
    tareWeight = undefined,
    capacity = undefined,
    capacityUnit = undefined,
    fabricationYear = undefined,
    firstUseDate = undefined,
    purchaseDate = undefined,
    customData = undefined,

    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
  }) {
    super(id, number || no, designation);
    this.resource = resource ? new Resource(resource) : null;
    this.active = active;
    this.tags = tags;
    this.planning = planning;
    this.serialNumber = serialNumber;
    this.chassisNumber = chassisNumber;
    this.registrationNumber = registrationNumber;
    this.memo = memo;
    this.model = model;
    this.power = power;
    this.tareWeight = tareWeight;
    this.capacity = capacity;
    this.capacityUnit = capacityUnit ? new Unit(capacityUnit) : null;
    this.fabricationYear = fabricationYear;
    this.firstUseDate = firstUseDate;
    this.purchaseDate = purchaseDate;
    this.customData = customData;
  }

  static get baseModuleForRedirection() {
    return 'equipment-base';
  }
  get baseModuleForRedirection() {
    return Equipment.baseModuleForRedirection;
  }

  static get fkName() {
    return 'equipment_id';
  }
  static get apiRoute() {
    return 'equipment';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }
  static get listPageRoute() {
    return 'equipmentList';
  }
  static get detailPageRoute() {
    return 'equipmentDetail';
  }
  get fkName() {
    return Equipment.fkName;
  }
  get apiRoute() {
    return Equipment.apiRoute;
  }
  get detailPageRoute() {
    return Equipment.detailPageRoute;
  }
  get listPageRoute() {
    return Equipment.listPageRoute;
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }

  getDefaultSort() {
    return Equipment.defaultSort;
  }

  static get printTemplateType() {
    return 'equipment';
  }
  get printTemplateType() {
    return Equipment.printTemplateType;
  }

  static get attrsToStoreInForm() {
    return ['number', 'designation', 'resource'];
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }

  static get joinAttrsKey() {
    return ['number', 'designation', 'resource'];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'active',
      'tags',
      'creationDate',
      'modificationDate',
    ]);
  }

  static get detailAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'resource',
      'tracker',
      'tags',
      'planning',
      'active',
      'serialNumber',
      'chassisNumber',
      'registrationNumber',
      'memo',
      'model',
      'power',
      'tareWeight',
      'capacity',
      'capacityUnit',
      'fabricationYear',
      'firstUseDate',
      'purchaseDate',
      'customData',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      ['number', 'designation', 'active', 'tags'],
      ['resource'],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'resource',
        'planning',
        'active',
        'tags',
      ]),
      'hit-base.bulk-update.advanced-information': this.getAttributes([
        'serialNumber',
        'chassisNumber',
        'registrationNumber',
        'model',
        'power',
        'tareWeight',
        'capacity',
        'capacityUnit',
        'fabricationYear',
        'firstUseDate',
        'purchaseDate',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }

  // Defines the list of all possible attributes for this model
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '1fr'}),
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '2fr'}),
      new AT.FkResourceAttribute({
        name: 'resource',
        joinPath: ['resource!fk_equipment_cost_resource_id_resource'],
        formItemName: 'resource',
        fkToUpdate: 'cost_resource_id',
        tableProperties: {
          header: 'hit-app.common.cost-resource',
        },
        filters: {resource_category_id: `eq.${RESOURCE_CATEGORIES.MACHINE}`},
      }),
      this.TAGS_ATTRIBUTE({moduleUsage: 'equipment', tableOrigin: 'equipment'}),
      new AT.BooleanAttribute({
        name: 'planning',
        tableProperties: {
          header: 'hit-app.timeslot.planning-flag',
        },
      }),
      new AT.FkTrackerCustomerAttribute({
        name: 'tracker',
        joinPath: ['tracker!fk_equipment_tracker_id_tracker'],
        supplementaryJoinColumns: ['fixedPosition'],
        tableProperties: {
          header: 'hit-app.common.tracker',
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
      new AT.StringAttribute({
        name: 'serialNumber',
        column: 'serial_no',
        tableProperties: {
          header: 'hit-app.equipment.serial-number',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'serialNumber',
        column: 'serial_no',
        tableProperties: {
          header: 'hit-app.equipment.serial-number',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'chassisNumber',
        column: 'chassis_no',
        tableProperties: {
          header: 'hit-app.equipment.chassis-number',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'registrationNumber',
        column: 'registration_no',
        tableProperties: {
          header: 'hit-app.equipment.registration-number',
          sharable: true,
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.StringAttribute({
        name: 'model',
        column: 'model',
        tableProperties: {
          header: 'hit-app.equipment.model',
          sharable: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'power',
        column: 'power',
        decimalPrecision: 2,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.equipment.power',
          sharable: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'tareWeight',
        column: 'tare_weight',
        decimalPrecision: 3,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.equipment.tare-weight',
          sharable: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'capacity',
        column: 'capacity',
        decimalPrecision: 3,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.equipment.capacity',
          sharable: true,
        },
      }),
      new AT.FkUnitAttribute({
        name: 'capacityUnit',
        joinPath: ['unit!fk_equipment_capacity_unit_id_unit'],
        fkToUpdate: 'capacity_unit_id',
        tableProperties: {
          header: 'hit-base.common.unit',
        },
      }),
      new AT.IntegerAttribute({
        name: 'fabricationYear',
        column: 'fabrication_year',
        tableProperties: {
          header: 'hit-app.equipment.fabrication-year',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'firstUseDate',
        column: 'first_use_date',
        tableProperties: {
          header: 'hit-app.equipment.first-use-date',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'purchaseDate',
        column: 'purchase_date',
        tableProperties: {
          header: 'hit-app.equipment.purchase-date',
          sharable: true,
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.ArrayAttribute({
        name: 'aclTrackingQualification',
        column: 'acl_tracking_qualification',
        tableProperties: {
          header: 'hit-base.common.tracking-access-roles',
        },
      }),
      new AT.ArrayAttribute({
        name: 'aclTrackingStaff',
        column: 'acl_tracking_staff',
        tableProperties: {
          header: 'hit-base.common.tracking-access-staff',
        },
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
      }),
    ];
  }
}
